import React, { useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, CircleMarker, GeoJSON, Pane, Popup, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Tooltip as MuiTooltip } from '@mui/material';
import 'leaflet/dist/leaflet.css';
import api from '../../services/api';
import styles from '../../assets/styles/LayoutShared.module.css';
import CartaoExpansivel from '../../components/Dashboard/CartaoExpansivel';
import AjustaTamanhoMapa from '../../components/GeoDraw/AjustaTamanhoMapa';
import { formatarKg, formatarNumero, formatarRendimento } from '../../utils/formatters';
import AvisoDadosDesatualizados from '../../components/Dashboard/AvisoDadosDesatualizados';

const CENTRO_PADRAO: [number, number] = [-22.9, -48.4]; // interior de SP, região onde os projetos-piloto atuam
const COR_CIRCULO = '#00c3ff';
const COR_DESTAQUE = '#0284c7';
const MARGEM_ENQUADRAMENTO: [number, number] = [40, 40];
const ZOOM_MAXIMO_ENQUADRAMENTO = 11;
const LIMITE_ROTULOS_TODOS = 8; // até 8 círculos: valor em todos; acima disso, só nos 5 maiores
const QTD_ROTULOS_TOP = 5;

// Raio proporcional à raiz quadrada da métrica escolhida, relativo ao maior círculo do nível
// (não linear, pra não distorcer a percepção de área). RAIO_MIN mantém os pequenos clicáveis.
const RAIO_MIN = 6;
const RAIO_MAX = 30;
function raioDoPonto(valor: number, maxValor: number): number {
  if (!(maxValor > 0) || !(valor > 0)) return RAIO_MIN;
  return RAIO_MIN + (RAIO_MAX - RAIO_MIN) * Math.sqrt(valor / maxValor);
}

type Indicador = 'kg' | 'pescadores' | 'rendimento' | 'registros';

const INDICADORES: { valor: Indicador; rotulo: string }[] = [
  { valor: 'kg', rotulo: 'Produção (kg)' },
  { valor: 'pescadores', rotulo: 'Informantes' },
  { valor: 'rendimento', rotulo: 'Rendimento (kg/pescador-dia)' },
  { valor: 'registros', rotulo: 'Registros' },
];

const ROTULO_TIPO: Record<string, string> = {
  bacia: 'Bacia',
  rio: 'Rio',
  represa: 'Represa',
  trecho_municipio: 'Trecho',
  area_pesca: 'Área',
  outro: 'Outro',
};

// Camadas de contexto opcionais, todas desligadas por padrão, abaixo dos círculos e sem clique.
const CAMADAS_CONTEXTO: { tipo: string; rotulo: string; cor: string }[] = [
  { tipo: 'bacia', rotulo: 'Bacias', cor: '#5a6570' },
  { tipo: 'rio', rotulo: 'Rios', cor: '#2b7bb9' },
  { tipo: 'trecho_municipio', rotulo: 'Trechos', cor: '#c2410c' },
];

interface Metricas {
  pesoKg: number;
  registros: number;
  pescadores: number;
  rendimento: number | null;
}

interface FilhoNivel extends Metricas {
  uid_pesca_local: string;
  nome: string;
  tipo: string;
  temFilhos: boolean;
  semCoordenadas: boolean;
}

interface ItemNivel extends Metricas {
  uid_pesca_local: string | null;
  nome: string;
  tipo: string | null;
  categoria: 'no' | 'proprio' | 'sem_vinculo';
  temFilhos: boolean;
  posicao: { lat: number; lng: number } | null;
  semCoordenadas: boolean;
  limites: [[number, number], [number, number]] | null;
  filhos: FilhoNivel[];
}

interface ResumoNivel {
  kgTotal: number;
  kgNoMapa: number;
  kgSemCoordenadas: number;
  kgSemLocalVinculado: number;
}

interface NivelMapa {
  caminho: { uid_pesca_local: string; nome: string }[];
  itens: ItemNivel[];
  resumo: ResumoNivel;
}

interface FiltrosMapaProducao {
  uid_pesca_especie: string | null;
  local?: string | null;
  ambiente: string | null;
  dataInicio: string;
  dataFim: string;
  uid_pesca_projetos: string[];
  uid_pesca_area: string | null;
  uid_pesca_pescador: string | null;
  uid_pesca_colonia: string | null;
  uid_pesca_local: string[];
  uid_pesca_apetrecho?: string | null;
  validacao?: 'todos' | 'somente_validados';
}

interface MapaProducaoProps {
  filtrosBase: FiltrosMapaProducao;
  /** Nome do local selecionado (ou "N locais"), mostrado no título. */
  tituloLocal?: string | null;
  /** Navega pro nó (mesmo efeito de clicar na barra do gráfico); null = "Todos". */
  onNavegar: (uid: string | null) => void;
}

const valorDe = (m: Metricas, indicador: Indicador): number => {
  switch (indicador) {
    case 'pescadores': return m.pescadores;
    case 'registros': return m.registros;
    case 'rendimento': return m.rendimento ?? 0;
    default: return m.pesoKg;
  }
};

// Valor sem unidade fixa por nível (a escolha kg/t fica em formatarValorLinha).
const formatarValorCurto = (valor: number, indicador: Indicador): string => {
  switch (indicador) {
    case 'kg': return `${formatarKg(valor)} kg`;
    case 'rendimento': return formatarRendimento(valor);
    default: return formatarNumero(valor, 0);
  }
};

// Mesma unidade pra todos os itens do nível: se o maior kg do nível passa de 10 mil, tudo em t (1 casa).
const formatarValorLinha = (m: Metricas, indicador: Indicador, emToneladas: boolean): string => {
  if (indicador === 'rendimento' && m.rendimento == null) return 'n/d';
  const valor = valorDe(m, indicador);
  if (indicador === 'kg') return emToneladas ? `${formatarNumero(valor / 1000, 1)} t` : `${formatarKg(valor)} kg`;
  return formatarValorCurto(valor, indicador);
};

// Centro do contorno (caixa envolvente) — só pras camadas de contexto de trechos.
function centroDaGeometria(geometria: any): [number, number] | null {
  let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
  const visitar = (c: any) => {
    if (!Array.isArray(c)) return;
    if (typeof c[0] === 'number' && typeof c[1] === 'number') {
      minLng = Math.min(minLng, c[0]); maxLng = Math.max(maxLng, c[0]);
      minLat = Math.min(minLat, c[1]); maxLat = Math.max(maxLat, c[1]);
    } else c.forEach(visitar);
  };
  visitar(geometria?.coordinates);
  return Number.isFinite(minLat) ? [(minLat + maxLat) / 2, (minLng + maxLng) / 2] : null;
}

const chaveDoItem = (i: ItemNivel) => (i.categoria === 'proprio' ? `${i.uid_pesca_local}:proprio` : i.uid_pesca_local ?? 'sem_vinculo');

// Reenquadra o mapa nos círculos do nível atual a cada carga de dados.
function AjustaEnquadramento({ versao, centros }: { versao: number; centros: [number, number][] }) {
  const map = useMap();
  useEffect(() => {
    if (versao === 0 || centros.length === 0) return;
    map.fitBounds(L.latLngBounds(centros), { padding: MARGEM_ENQUADRAMENTO, maxZoom: ZOOM_MAXIMO_ENQUADRAMENTO });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versao, map]);
  return null;
}

interface CandidatoRotulo { chave: string; pos: [number, number]; texto: string }

// Escolhe quais círculos mostram rótulo: todos se houver até 8, senão só os 5 maiores — e, em
// qualquer caso, descarta o rótulo do círculo menor quando colidir com um já aceito.
function ColisaoRotulos({ candidatos, onChange }: { candidatos: CandidatoRotulo[]; onChange: (s: Set<string>) => void }) {
  const map = useMap();
  const assinatura = candidatos.map((c) => `${c.chave}:${c.texto}:${c.pos[0].toFixed(4)},${c.pos[1].toFixed(4)}`).join('|');
  useEffect(() => {
    const calcular = () => {
      const base = candidatos.length <= LIMITE_ROTULOS_TODOS ? candidatos : candidatos.slice(0, QTD_ROTULOS_TOP);
      const aceitos: { x1: number; y1: number; x2: number; y2: number }[] = [];
      const escolhidos = new Set<string>();
      for (const c of base) { // já vem do maior pro menor
        const p = map.latLngToContainerPoint(c.pos);
        const larg = c.texto.length * 6.6 + 8;
        const r = { x1: p.x - larg / 2, x2: p.x + larg / 2, y1: p.y - 8, y2: p.y + 8 };
        const colide = aceitos.some((a) => r.x1 < a.x2 && r.x2 > a.x1 && r.y1 < a.y2 && r.y2 > a.y1);
        if (!colide) { aceitos.push(r); escolhidos.add(c.chave); }
      }
      onChange(escolhidos);
    };
    calcular();
    map.on('zoomend moveend resize', calcular);
    return () => { map.off('zoomend moveend resize', calcular); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, assinatura]);
  return null;
}

const MapaProducao: React.FC<MapaProducaoProps> = ({ filtrosBase, tituloLocal, onNavegar }) => {
  const [indicador, setIndicador] = useState<Indicador>('kg');
  const [nivel, setNivel] = useState<NivelMapa | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [tentativa, setTentativa] = useState(0); // "Tentar de novo" refaz a busca sem mudar os filtros
  const [versaoDados, setVersaoDados] = useState(0);
  const [destaque, setDestaque] = useState<string | null>(null);
  const [rotulosVisiveis, setRotulosVisiveis] = useState<Set<string>>(new Set());
  const [expandidos, setExpandidos] = useState<Set<string>>(new Set());
  const [menuCamadas, setMenuCamadas] = useState(false);
  const [camadasAtivas, setCamadasAtivas] = useState<string[]>([]);
  const [nosContexto, setNosContexto] = useState<any[]>([]);

  // O mapa segue o nível do gráfico "Produção por local": 1 local = uid_pai (filhos dele),
  // vários = comparar, nenhum = raízes. uid_pesca_local não vai como filtro.
  useEffect(() => {
    setCarregando(true);
    const query = new URLSearchParams();
    if (filtrosBase.uid_pesca_especie) query.append('uid_pesca_especie', filtrosBase.uid_pesca_especie);
    if (filtrosBase.local) query.append('local', filtrosBase.local);
    if (filtrosBase.ambiente) query.append('ambiente', filtrosBase.ambiente);
    if (filtrosBase.dataInicio) query.append('dataInicio', filtrosBase.dataInicio);
    if (filtrosBase.dataFim) query.append('dataFim', filtrosBase.dataFim);
    if (filtrosBase.uid_pesca_projetos.length) query.append('uid_pesca_projetos', filtrosBase.uid_pesca_projetos.join(','));
    if (filtrosBase.uid_pesca_area) query.append('uid_pesca_area', filtrosBase.uid_pesca_area);
    if (filtrosBase.uid_pesca_pescador) query.append('uid_pesca_pescador', filtrosBase.uid_pesca_pescador);
    if (filtrosBase.uid_pesca_colonia) query.append('uid_pesca_colonia', filtrosBase.uid_pesca_colonia);
    if (filtrosBase.uid_pesca_apetrecho) query.append('uid_pesca_apetrecho', filtrosBase.uid_pesca_apetrecho);
    if (filtrosBase.validacao === 'somente_validados') query.append('validacao', filtrosBase.validacao);
    if (filtrosBase.uid_pesca_local.length === 1) query.append('uid_pai', filtrosBase.uid_pesca_local[0]);
    else if (filtrosBase.uid_pesca_local.length > 1) query.append('comparar', filtrosBase.uid_pesca_local.join(','));
    let ativo = true;

    api.get(`/pesca/stats/mapa-nivel?${query.toString()}`)
      .then((res) => {
        if (!ativo) return;
        const dados: NivelMapa = res.data.data;
        setNivel(dados);
        setErro(null);
        // abre por padrão os 3 maiores itens que têm netos
        setExpandidos(new Set(dados.itens.filter((i) => i.filhos.length > 0).slice(0, 3).map(chaveDoItem)));
        setVersaoDados((v) => v + 1);
      })
      // mantém o nível anterior no mapa e avisa que está desatualizado
      .catch((e) => { if (ativo) setErro(e?.response?.data?.message || 'Falha ao carregar o mapa.'); })
      .finally(() => { if (ativo) setCarregando(false); });
    return () => { ativo = false; };
  }, [filtrosBase, tentativa]);

  // Camadas de contexto: só busca a geometria dos nós quando alguma está ligada.
  useEffect(() => {
    if (camadasAtivas.length === 0 || nosContexto.length > 0) return;
    api.get('/pesca/locais/mapa')
      .then((res) => setNosContexto(res.data.data.nos || []))
      .catch(() => setNosContexto([]));
  }, [camadasAtivas, nosContexto.length]);

  const alternarCamada = (tipo: string) =>
    setCamadasAtivas((prev) => (prev.includes(tipo) ? prev.filter((x) => x !== tipo) : [...prev, tipo]));
  const alternarExpandido = (chave: string) =>
    setExpandidos((prev) => { const n = new Set(prev); if (n.has(chave)) n.delete(chave); else n.add(chave); return n; });

  const itens = useMemo(() => nivel?.itens ?? [], [nivel]);
  // Maior primeiro (desenhado por baixo): os menores entram depois, por cima.
  const circulos = useMemo(
    () => itens.filter((i) => i.posicao).sort((a, b) => valorDe(b, indicador) - valorDe(a, indicador)),
    [itens, indicador]
  );
  const maxValor = circulos.length ? valorDe(circulos[0], indicador) : 0;
  const emToneladas = indicador === 'kg' && itens.reduce((m, i) => Math.max(m, i.pesoKg), 0) >= 10000;
  const maxValorLista = itens.reduce((m, i) => Math.max(m, valorDe(i, indicador)), 0);
  // Enquadramento: posição de cada círculo + caixa de todos os descendentes com produção e coordenadas.
  const centros = circulos.flatMap((i) => [[i.posicao!.lat, i.posicao!.lng] as [number, number], ...(i.limites ?? [])]);
  const candidatosRotulo: CandidatoRotulo[] = circulos
    .filter((i) => valorDe(i, indicador) > 0)
    .map((i) => ({ chave: chaveDoItem(i), pos: [i.posicao!.lat, i.posicao!.lng], texto: formatarValorLinha(i, indicador, emToneladas) }));

  const resumo = nivel?.resumo;
  const tituloIndicador = INDICADORES.find((i) => i.valor === indicador)!.rotulo;
  const pctNoMapa = resumo && resumo.kgTotal > 0 ? (resumo.kgNoMapa / resumo.kgTotal) * 100 : null;
  const explicacaoCobertura = resumo
    ? `Fora do mapa: ${formatarKg(resumo.kgSemLocalVinculado)} kg sem local vinculado e ${formatarKg(resumo.kgSemCoordenadas)} kg em locais sem coordenadas (marcados "sem coordenadas" na lista).`
    : '';
  const modoComparacao = filtrosBase.uid_pesca_local.length > 1;

  const linhaArvore = (
    chave: string, nome: string, tipo: string | null, m: Metricas, opcoes: { uid: string | null; nivel: number; caret?: React.ReactNode; semCoord?: boolean; nota?: string; destaqueChave: string }
  ) => {
    const valor = valorDe(m, indicador);
    const pct = maxValorLista > 0 ? Math.max(0, Math.min(100, (valor / maxValorLista) * 100)) : 0;
    const clicavel = !!opcoes.uid;
    const emDestaque = destaque === opcoes.destaqueChave;
    return (
      <div
        key={chave}
        onMouseEnter={() => setDestaque(opcoes.destaqueChave)}
        onMouseLeave={() => setDestaque(null)}
        onClick={() => clicavel && onNavegar(opcoes.uid)}
        style={{ padding: '3px 4px 3px ' + (4 + opcoes.nivel * 14) + 'px', borderRadius: 6, cursor: clicavel ? 'pointer' : 'default', background: emDestaque ? '#e6f6ff' : 'transparent' }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 4 }}>
          <span style={{ width: 12, flex: '0 0 12px' }}>{opcoes.caret}</span>
          <span style={{ flex: 1, minWidth: 0, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', overflowWrap: 'anywhere', lineHeight: 1.25, fontWeight: opcoes.nivel === 0 ? 700 : 500, color: '#333' }} title={nome}>
            {nome}{tipo && <span style={{ fontSize: '0.66rem', fontWeight: 400, color: '#999' }}> {ROTULO_TIPO[tipo] || tipo}</span>}
          </span>
          <span style={{ fontSize: '0.74rem', color: '#444', whiteSpace: 'nowrap' }}>{formatarValorLinha(m, indicador, emToneladas)}</span>
        </div>
        <div style={{ marginLeft: 16, height: 4, background: '#f0f2f4', borderRadius: 2 }}>
          <div style={{ width: `${pct}%`, height: '100%', background: opcoes.semCoord ? '#c9ced3' : COR_CIRCULO, borderRadius: 2 }} />
        </div>
        {(opcoes.semCoord || opcoes.nota) && (
          <div style={{ marginLeft: 16, fontSize: '0.64rem', fontStyle: 'italic', color: '#a15c00' }}>{opcoes.nota || 'sem coordenadas'}</div>
        )}
      </div>
    );
  };

  return (
    <div className={styles.sectionCard} style={{ minHeight: '450px', marginTop: '10px' }}>
      <style>{`
        .rotulo-mapa-pesca.leaflet-tooltip { background: transparent; border: none; box-shadow: none; padding: 0; font-size: 11px; font-weight: 700; color: #04364a; text-shadow: 0 0 3px #fff, 0 0 3px #fff; }
        .rotulo-mapa-pesca.leaflet-tooltip::before { display: none; }
      `}</style>
      <div className={styles.sectionHeader}>
        Mapa da pesca{tituloLocal ? ` · ${tituloLocal}` : ''}
      </div>
      <CartaoExpansivel titulo="Mapa da pesca" alturaNormal={400}>
        {(alturaMapa) => (
          <>
            <div role="tablist" aria-label="Indicador do mapa" style={{ padding: '12px 16px 0', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {INDICADORES.map((v) => {
                const ativo = indicador === v.valor;
                return (
                  <button
                    key={v.valor}
                    type="button"
                    role="tab"
                    aria-selected={ativo}
                    onClick={() => setIndicador(v.valor)}
                    style={{
                      padding: '6px 12px', borderRadius: '16px', fontSize: '0.8rem', cursor: 'pointer',
                      border: ativo ? '1px solid #0284c7' : '1px solid #ddd',
                      background: ativo ? '#e6f6ff' : '#fff',
                      color: ativo ? '#026e8c' : '#555',
                      fontWeight: ativo ? 700 : 500,
                    }}
                  >
                    {v.rotulo}
                  </button>
                );
              })}
            </div>

            <div style={{ padding: '10px 16px 0' }}>
              <AvisoDadosDesatualizados erro={!!erro} comDadosAnteriores={!!nivel} detalhe={erro} carregando={carregando} onTentarDeNovo={() => setTentativa((t) => t + 1)} />
            </div>

            {/* BREADCRUMB (sincronizado com o do gráfico: os dois leem o mesmo filtro de local) */}
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px', padding: '10px 16px 0', fontSize: '0.85rem', color: '#666' }}>
              {filtrosBase.uid_pesca_local.length === 0 ? (
                <strong style={{ color: '#333' }}>Todos</strong>
              ) : (
                <button type="button" onClick={() => onNavegar(null)} style={breadcrumbBtnStyle}>Todos</button>
              )}
              {modoComparacao && (
                <>
                  <span style={{ color: '#aaa' }}>&gt;</span>
                  <strong style={{ color: '#333' }}>{filtrosBase.uid_pesca_local.length} locais selecionados (comparação)</strong>
                </>
              )}
              {!modoComparacao && (nivel?.caminho ?? []).map((no, idx, arr) => (
                <React.Fragment key={no.uid_pesca_local}>
                  <span style={{ color: '#aaa' }}>&gt;</span>
                  {idx === arr.length - 1 ? (
                    <strong style={{ color: '#333' }}>{no.nome}</strong>
                  ) : (
                    <button type="button" onClick={() => onNavegar(no.uid_pesca_local)} style={breadcrumbBtnStyle}>{no.nome}</button>
                  )}
                </React.Fragment>
              ))}
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', padding: '10px 16px 16px', alignItems: 'flex-start' }}>
              <div style={{ position: 'relative', flex: '1 1 480px', height: alturaMapa, minHeight: '320px', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.12)' }}>
                <MapContainer center={CENTRO_PADRAO} zoom={7} style={{ width: '100%', height: '100%' }}>
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <AjustaTamanhoMapa gatilho={alturaMapa} />
                  <AjustaEnquadramento versao={versaoDados} centros={centros} />
                  <ColisaoRotulos candidatos={candidatosRotulo} onChange={(s) => setRotulosVisiveis((prev) => (prev.size === s.size && Array.from(s).every((c) => prev.has(c)) ? prev : s))} />

                  {/* Camadas de contexto: pane próprio, abaixo dos círculos, sem clique */}
                  <Pane name="contexto" style={{ zIndex: 380 }}>
                    {CAMADAS_CONTEXTO.filter((c) => camadasAtivas.includes(c.tipo)).flatMap((c) =>
                      nosContexto
                        .filter((no) => no.tipo === c.tipo && no.geometria)
                        .map((no) => {
                          if (c.tipo === 'trecho_municipio') {
                            const centro = centroDaGeometria(no.geometria);
                            return centro ? (
                              <CircleMarker
                                key={`ctx-${no.uid_pesca_local}`}
                                center={centro}
                                radius={4}
                                pane="contexto"
                                interactive={false}
                                pathOptions={{ color: '#ffffff', weight: 1, fillColor: c.cor, fillOpacity: 1 }}
                              />
                            ) : null;
                          }
                          const bacia = c.tipo === 'bacia';
                          return (
                            <GeoJSON
                              key={`ctx-${no.uid_pesca_local}`}
                              data={no.geometria}
                              pane="contexto"
                              interactive={false}
                              style={() => (bacia
                                ? { color: c.cor, weight: 2, opacity: 1, fillColor: c.cor, fillOpacity: 0.08 }
                                : { color: c.cor, weight: 2.5, opacity: 0.8, fillOpacity: 0 })}
                              pointToLayer={(_f, latlng) => L.circleMarker(latlng, { radius: 4, pane: 'contexto', interactive: false, color: '#ffffff', weight: 1, fillColor: c.cor, fillOpacity: 1 })}
                            />
                          );
                        })
                    )}
                  </Pane>

                  {/* Um círculo por item do nível, na posição calculada no backend */}
                  {circulos.map((item) => {
                    const chave = chaveDoItem(item);
                    const emDestaque = destaque === chave;
                    const desce = item.categoria === 'no' && item.temFilhos;
                    const valor = valorDe(item, indicador);
                    return (
                      <CircleMarker
                        key={`${chave}-${indicador}`}
                        center={[item.posicao!.lat, item.posicao!.lng]}
                        radius={raioDoPonto(valor, maxValor)}
                        pathOptions={{
                          color: emDestaque ? COR_DESTAQUE : '#ffffff',
                          weight: emDestaque ? 3 : 1,
                          fillColor: COR_CIRCULO,
                          fillOpacity: emDestaque ? 0.85 : 0.6,
                        }}
                        eventHandlers={{
                          click: () => { if (desce) onNavegar(item.uid_pesca_local); },
                          mouseover: () => setDestaque(chave),
                          mouseout: () => setDestaque((d) => (d === chave ? null : d)),
                        }}
                      >
                        {rotulosVisiveis.has(chave) && (
                          <Tooltip permanent direction="center" className="rotulo-mapa-pesca" interactive={false}>
                            {formatarValorLinha(item, indicador, emToneladas)}
                          </Tooltip>
                        )}
                        {!desce && (
                          <Popup>
                            <strong>{item.nome}</strong> {item.tipo && <span style={{ color: '#888' }}>({ROTULO_TIPO[item.tipo] || item.tipo})</span>}<br />
                            Produção: {formatarKg(item.pesoKg)} kg<br />
                            Informantes: {formatarNumero(item.pescadores, 0)}<br />
                            Rendimento: {item.rendimento == null ? 'n/d' : `${formatarRendimento(item.rendimento)} kg/pescador-dia`}<br />
                            Registros: {formatarNumero(item.registros, 0)}
                          </Popup>
                        )}
                      </CircleMarker>
                    );
                  })}
                </MapContainer>

                {/* Botão de camadas (fora do MapContainer: não rouba clique do mapa) */}
                <div style={{ position: 'absolute', top: 10, right: 10, zIndex: 1000 }}>
                  <button
                    type="button"
                    onClick={() => setMenuCamadas((a) => !a)}
                    aria-expanded={menuCamadas}
                    style={{ padding: '5px 10px', fontSize: '0.78rem', borderRadius: 6, border: '1px solid #bbb', background: '#fff', cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.2)' }}
                  >
                    Camadas {menuCamadas ? '▴' : '▾'}{camadasAtivas.length > 0 ? ` (${camadasAtivas.length})` : ''}
                  </button>
                  {menuCamadas && (
                    <div style={{ marginTop: 4, padding: '8px 10px', background: '#fff', border: '1px solid #ccc', borderRadius: 6, boxShadow: '0 2px 8px rgba(0,0,0,0.2)', fontSize: '0.78rem' }}>
                      {CAMADAS_CONTEXTO.map((c) => (
                        <label key={c.tipo} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, cursor: 'pointer' }}>
                          <input type="checkbox" checked={camadasAtivas.includes(c.tipo)} onChange={() => alternarCamada(c.tipo)} />
                          {c.rotulo}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* PAINEL LATERAL: tamanho, cobertura, referência e árvore do nível */}
              <div style={{ width: '270px', flexShrink: 0, height: alturaMapa, minHeight: '320px', display: 'flex', flexDirection: 'column', fontSize: '0.78rem', color: '#555' }}>
                <p style={{ fontWeight: 700, fontSize: '0.82rem', margin: '0 0 6px', color: '#333' }}>Tamanho = {tituloIndicador}</p>
                <p style={{ margin: '0 0 10px', lineHeight: 1.4 }}>
                  {carregando && !resumo ? 'Carregando...' : resumo && pctNoMapa != null ? (
                    <>
                      No mapa: <strong>{formatarKg(resumo.kgNoMapa)}</strong> de {formatarKg(resumo.kgTotal)} kg ({formatarNumero(pctNoMapa, 0)}%){' '}
                      <MuiTooltip title={explicacaoCobertura} arrow>
                        <span aria-label="Explicação da cobertura" style={{ cursor: 'help', color: '#0284c7', fontWeight: 700, border: '1px solid #0284c7', borderRadius: '50%', display: 'inline-block', width: 14, height: 14, lineHeight: '13px', textAlign: 'center', fontSize: '0.68rem' }}>i</span>
                      </MuiTooltip>
                    </>
                  ) : 'Nenhum ponto com os filtros atuais.'}
                </p>

                <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', borderTop: '1px solid #eee', paddingTop: 6 }}>
                  {itens.length === 0 && !carregando && <span style={{ color: '#999' }}>Sem produção para os filtros atuais.</span>}
                  {itens.map((item) => {
                    const chave = chaveDoItem(item);
                    const aberto = expandidos.has(chave);
                    const temNetos = item.filhos.length > 0;
                    return (
                      <div key={chave}>
                        {linhaArvore(chave, item.nome, item.tipo, item, {
                          uid: item.categoria === 'no' ? item.uid_pesca_local : null,
                          nivel: 0,
                          destaqueChave: chave,
                          semCoord: item.semCoordenadas && item.categoria !== 'sem_vinculo',
                          nota: item.categoria === 'sem_vinculo' ? 'fora do mapa: sem local vinculado' : undefined,
                          caret: temNetos ? (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); alternarExpandido(chave); }}
                              aria-label={aberto ? 'Recolher' : 'Expandir'}
                              style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, fontSize: '0.7rem', color: '#666' }}
                            >{aberto ? '▾' : '▸'}</button>
                          ) : null,
                        })}
                        {aberto && item.filhos.map((f) => linhaArvore(`${chave}>${f.uid_pesca_local}`, f.nome, f.tipo, f, {
                          uid: f.uid_pesca_local, nivel: 1, destaqueChave: chave, semCoord: f.semCoordenadas,
                        }))}
                      </div>
                    );
                  })}
                </div>

                <p style={{ fontSize: '0.68rem', color: '#999', margin: '8px 0 0', lineHeight: 1.4 }}>
                  Clique num local para descer um nível e filtrar o Dashboard.
                </p>
              </div>
            </div>
          </>
        )}
      </CartaoExpansivel>
    </div>
  );
};

const breadcrumbBtnStyle: React.CSSProperties = {
  border: 'none', background: 'transparent', color: '#0a6c8f', cursor: 'pointer', padding: 0, fontSize: '0.85rem', textDecoration: 'underline',
};

export default MapaProducao;
